import { Injectable } from '@angular/core';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';

export interface BiometricStatus {
  isAvailable: boolean;
  biometryType: BiometryType;
  isEnabled: boolean;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
  private readonly SERVER_IDENTIFIER = 'com.uspark.mobile';
  private readonly BIOMETRIC_ENABLED_KEY = 'biometricEnabled';

  constructor(private platform: Platform) {}

  async checkAvailability(): Promise<BiometricStatus> {
    // Wait for platform to be ready
    await this.platform.ready();

    const isCapacitor = this.platform.is('capacitor');
    const isHybrid = this.platform.is('hybrid');
    const isIos = this.platform.is('ios');
    const isAndroid = this.platform.is('android');

    console.log('Biometric check - Platform info:', { isCapacitor, isHybrid, isIos, isAndroid });

    // Only check on native platforms
    if (!isCapacitor && !isHybrid) {
      console.log('Biometric check - Not running on native platform');
      return { isAvailable: false, biometryType: BiometryType.NONE, isEnabled: false };
    }

    try {
      console.log('Biometric check - Calling NativeBiometric.isAvailable()');
      const result = await NativeBiometric.isAvailable();
      console.log('Biometric check - Result:', result);

      const isEnabled = await this.isBiometricEnabled();

      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
        isEnabled
      };
    } catch (error) {
      console.error('Biometric check - Error:', error);
      return {
        isAvailable: false,
        biometryType: BiometryType.NONE,
        isEnabled: false,
        errorMessage: (error as Error).message
      };
    }
  }

  async isBiometricEnabled(): Promise<boolean> {
    const result = await Preferences.get({ key: this.BIOMETRIC_ENABLED_KEY });
    return result.value === 'true';
  }

  async enableBiometric(email: string, password: string): Promise<boolean> {
    try {
      // First verify the user can authenticate with biometric
      await NativeBiometric.verifyIdentity({
        reason: 'Confirm your identity to enable quick login',
        title: 'Enable Biometric Login',
        subtitle: 'Verify your identity',
        description: 'Touch the fingerprint sensor or look at the camera'
      });

      // Store credentials securely
      await NativeBiometric.setCredentials({
        username: email,
        password: password,
        server: this.SERVER_IDENTIFIER
      });

      // Mark biometric as enabled
      await Preferences.set({
        key: this.BIOMETRIC_ENABLED_KEY,
        value: 'true'
      });

      return true;
    } catch (error) {
      console.error('Failed to enable biometric:', error);
      return false;
    }
  }

  async disableBiometric(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({
        server: this.SERVER_IDENTIFIER
      });
    } catch (error) {
      // Credentials may not exist, ignore
    }

    await Preferences.set({
      key: this.BIOMETRIC_ENABLED_KEY,
      value: 'false'
    });
  }

  async authenticateAndGetCredentials(): Promise<{ email: string; password: string } | null> {
    try {
      // Verify identity first
      await NativeBiometric.verifyIdentity({
        reason: 'Log in to USPARK',
        title: 'Login with Biometric',
        subtitle: 'Verify your identity',
        description: 'Touch the fingerprint sensor or look at the camera'
      });

      // Get stored credentials
      const credentials = await NativeBiometric.getCredentials({
        server: this.SERVER_IDENTIFIER
      });

      return {
        email: credentials.username,
        password: credentials.password
      };
    } catch (error) {
      console.error('Biometric authentication failed:', error);
      return null;
    }
  }

  getBiometryTypeName(type: BiometryType): string {
    switch (type) {
      case BiometryType.FACE_ID:
        return 'Face ID';
      case BiometryType.TOUCH_ID:
        return 'Touch ID';
      case BiometryType.FINGERPRINT:
        return 'Fingerprint';
      case BiometryType.FACE_AUTHENTICATION:
        return 'Face Recognition';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'Iris';
      default:
        return 'Biometric';
    }
  }
}
